import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import "./CoToNekrolog.scss";

const SECTIONS = [
  {
    title: "Imię i nazwisko oraz data urodzenia i śmierci",
    desc: "Pełne dane identyfikacyjne Zmarłego - podstawa każdego nekrologu. Daty pozwalają czytelnikom zrozumieć wiek i czas odejścia.",
  },
  {
    title: "Informacje o ceremonii pogrzebowej",
    desc: "Dokładna data, godzina i miejsce Mszy świętej oraz miejsce pochówku. To najważniejsza informacja dla osób chcących oddać hołd.",
  },
  {
    title: "Informacja o rodzinie",
    desc: 'Zazwyczaj w formie "O czym zawiadamia pogrążona w żałobie rodzina" z wymienieniem najbliższych - dzieci, wnuków, rodzeństwa.',
  },
  {
    title: "Cytat lub modlitwa",
    desc: "Krótki werset biblijny, ulubiony cytat Zmarłego lub modlitwa. Nadaje nekrologowi osobisty i duchowy charakter.",
  },
  {
    title: "Informacja o różańcu",
    desc: "Jeśli organizowany jest różaniec przed pogrzebem - jego godzina i intencja. Pozwala wiernym uczestniczyć w modlitwie.",
  },
];

const TIPS = [
  {
    num: "01",
    title: "Pisz prosto i godnie",
    desc: "Nekrolog to oficjalny komunikat. Unikaj przesadnie emocjonalnego języka - prostota i powaga są tu największą wartością.",
  },
  {
    num: "02",
    title: "Sprawdź daty i miejsca",
    desc: "Błąd w dacie lub adresie kościoła może spowodować, że bliscy nie dotrą na ceremonię. Weryfikuj każdy szczegół.",
  },
  {
    num: "03",
    title: "Dopasuj formę do medium",
    desc: "Nekrolog prasowy rządzi się innymi prawami niż nekrolog na tablicę ogłoszeń. Dostosuj format i rozmiar czcionki do miejsca ekspozycji.",
  },
  {
    num: "04",
    title: "Zadbaj o czytelność",
    desc: "Wyraźna czcionka, odpowiedni kontrast i właściwe rozmieszczenie treści sprawiają, że nekrolog można odczytać z odległości.",
  },
  {
    num: "05",
    title: "Skorzystaj z gotowego generatora",
    desc: "Zamiast projektować od zera - użyj sprawdzonego narzędzia, które prowadzi Cię krok po kroku i generuje gotowy plik do druku.",
  },
];

const CoToNekrolog = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <main className="ctn-page">
      <Helmet>
        <title>
          Co to jest nekrolog? Jak napisać nekrolog - poradnik | Nekrodruk.pl
        </title>
        <meta
          name="description"
          content="Nekrolog to oficjalny komunikat o śmierci i pogrzebie. Dowiedz się, co powinien zawierać, jak go napisać i jak szybko wygenerować profesjonalny nekrolog online."
        />
        <meta
          name="keywords"
          content="co to nekrolog, jak napisać nekrolog, nekrolog wzór, nekrolog co zawiera, generator nekrologów, nekrolog online, nekrolog do druku"
        />
        <meta name="author" content="Nekrodruk.pl" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://nekrodruk.pl/co-to-nekrolog" />
      </Helmet>

      {/* HERO */}
      <section className="ctn-hero">
        <div className="ctn-hero__inner">
          <span className="ctn-hero__label">Poradnik</span>
          <h1 className="ctn-hero__title">Co to jest nekrolog?</h1>
          <p className="ctn-hero__lead">
            Nekrolog to oficjalny komunikat o śmierci i uroczystościach
            pogrzebowych, publikowany w prasie lub wywieszany jako plakat. Jest
            jednym z pierwszych kroków, jakie wykonuje rodzina po stracie
            bliskiej osoby - i zasługuje na staranne przygotowanie.
          </p>
        </div>
      </section>

      {/* DEFINICJA */}
      <section className="ctn-section ctn-section--white">
        <div className="ctn-container">
          <div className="ctn-definition">
            <div className="ctn-definition__text">
              <h2>Definicja i historia nekrologu</h2>
              <p>
                Słowo <em>nekrolog</em> pochodzi z greki: <em>nekros</em>{" "}
                (martwy) i <em>logos</em> (słowo). Przez wieki był to krótki
                tekst upamiętniający Zmarłego - dziś najczęściej przybiera formę
                graficzną z krzyżem, imieniem i nazwiskiem oraz informacją o
                ceremonii.
              </p>
              <p>
                W Polsce nekrolog jest głęboko zakorzeniony w tradycji
                katolickiej i funeralnej. Zakłady pogrzebowe, drukarnie i
                redakcje gazet od dawna pomagają rodzinom w jego przygotowaniu -
                dziś można to zrobić szybciej i taniej dzięki internetowi.
              </p>
            </div>
            <div className="ctn-definition__box">
              <div className="ctn-definition__quote">
                <span className="ctn-definition__quote-mark">"</span>
                Nekrolog to nie tylko ogłoszenie - to ostatnie publiczne
                pożegnanie, które powinno oddawać godność i szacunek dla Osoby,
                która odeszła.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CO ZAWIERA */}
      <section className="ctn-section ctn-section--light">
        <div className="ctn-container">
          <h2 className="ctn-section__title">Co powinien zawierać nekrolog?</h2>
          <p className="ctn-section__subtitle">
            Każdy nekrolog składa się z kilku kluczowych elementów. Pominięcie
            któregokolwiek może sprawić, że ceremonia odbędzie się bez ważnych
            dla rodziny osób.
          </p>
          <div className="ctn-cards">
            {SECTIONS.map((s, i) => (
              <div key={i} className="ctn-card">
                <h3 className="ctn-card__title">{s.title}</h3>
                <p className="ctn-card__desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JAK PISAC */}
      <section className="ctn-section ctn-section--white">
        <div className="ctn-container">
          <h2 className="ctn-section__title">Jak napisać dobry nekrolog?</h2>
          <p className="ctn-section__subtitle">
            Kilka zasad, które pomogą Ci przygotować nekrolog, który będzie
            czytelny, godny i kompletny - nawet w trudnym czasie żałoby.
          </p>
          <div className="ctn-tips">
            {TIPS.map((tip, i) => (
              <div key={i} className="ctn-tip">
                <span className="ctn-tip__num">{tip.num}</span>
                <div className="ctn-tip__content">
                  <h3>{tip.title}</h3>
                  <p>{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DLACZEGO NEKRODRUK */}
      <section className="ctn-section ctn-section--light">
        <div className="ctn-container">
          <h2 className="ctn-section__title">
            Dlaczego warto skorzystać z Nekrodruk.pl?
          </h2>
          <div className="ctn-benefits">
            <div className="ctn-benefit">
              <h3>Szybko - w kilka minut</h3>
              <p>
                Wypełnij formularz z danymi Zmarłego, a system automatycznie
                ułoży tekst i wygeneruje gotowy plik PDF do druku.
              </p>
            </div>
            <div className="ctn-benefit">
              <h3>Profesjonalne szablony</h3>
              <p>
                Kilka eleganckich projektów nekrologów - minimalistyczny,
                czarny, z pogrubionymi danymi - dopasowanych do różnych potrzeb
                drukarni.
              </p>
            </div>
            <div className="ctn-benefit">
              <h3>Kompletna treść bez błędów</h3>
              <p>
                System pilnuje, by żaden kluczowy element nie został pominięty -
                daty, godziny, miejsca, imiona rodziny.
              </p>
            </div>
            <div className="ctn-benefit">
              <h3>Gotowy do druku od razu</h3>
              <p>
                Plik PNG lub PDF w wysokiej rozdzielczości - gotowy do wysłania
                do drukarni lub wydrukowania na miejscu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctn-cta">
        <div className="ctn-container">
          <h2 className="ctn-cta__title">
            Wypróbuj generator nekrologów za darmo
          </h2>
          <p className="ctn-cta__desc">
            Bez zakładania konta, bez opłat. Przekonaj się, jak szybko i prosto
            można przygotować profesjonalny nekrolog online.
          </p>
          <button className="ctn-cta__btn" onClick={() => navigate("/demo")}>
            Wypróbuj demo →
          </button>
        </div>
      </section>
    </main>
  );
};

export default CoToNekrolog;
